import {useEffect} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {
  selectBuildingsDamage,
  selectCitiesCache,
  selectCurrentPlayer,
  selectHexes,
  selectPendingSupply,
  selectPhase,
  selectPlayers,
  selectSelectedFarmsForConversion,
  selectSelectedUnit,
  selectSpecialBuildings,
  selectUnits,
  selectWarPhaseStage
} from "../../redux/reducers/sharedStateReducer/selectors";
import {usePrev} from "../../hooks/react/usePrev";
import {PHASES, WAR_PHASE_STAGES} from "../../constants/phases";
import {deselectFarm, deselectUnit} from "../../redux/actions";
import UNIT_TYPES from "../../configs/units";
import {selectSelectedFarmHexKey} from "../../redux/reducers/privateStateReducer/selectors";
import {calcFarmRemainingSupply} from "../../utils/gameState/calcFarmRemainingSupply";
import {checkIfUnitMeleeElf} from "../../utils/factions/elves/checkIfUnitMeleeElf";
import {isUnitFaction} from "../../utils/factions/isUnitFaction";
import {FACTIONS} from "../../constants/factions";
import {
  selectIsParallelSupplyPhase,
  selectSupplyActingPlayer
} from "../../utils/gameState/supply/parallelSupplySelectors";

const EntitiesAutoSelector = () => {
  const dispatch = useDispatch();
  const phase = useSelector(selectPhase);
  const warPhaseStage = useSelector(selectWarPhaseStage);
  const prevWarPhaseStage = usePrev(warPhaseStage);
  const selectedUnit = useSelector(selectSelectedUnit);
  const currentPlayer = useSelector(selectCurrentPlayer);
  const selectedFarmHexKey = useSelector(selectSelectedFarmHexKey);
  const hexes = useSelector(selectHexes);
  const units = useSelector(selectUnits);
  const citiesCache = useSelector(selectCitiesCache);
  const specialBuildings = useSelector(selectSpecialBuildings);
  const buildingsDamage = useSelector(selectBuildingsDamage)
  const players = useSelector(selectPlayers);
  const pendingSupply = useSelector(selectPendingSupply);
  // Остаток снабжения выбранной фермы считается за игрока, раздающего
  // снабжение: в одновременной фазе снабжения сетевой игры — своего (см.
  // selectSupplyActingPlayer), в поочередной — текущего, как всегда
  const supplyPlayer = useSelector(selectSupplyActingPlayer);
  const isParallelSupplyPhase = useSelector(selectIsParallelSupplyPhase);
  const selectedFarmsForConversion = useSelector((state) => selectSelectedFarmsForConversion(
    state, selectSupplyActingPlayer(state)?.id ?? null
  ));

  useEffect(() => {
    if (selectedUnit && phase === PHASES.WAR && warPhaseStage !== prevWarPhaseStage) {
      const isCurrentPlayerOwner = selectedUnit.ownerId === currentPlayer.id;

      switch (warPhaseStage) {
        case WAR_PHASE_STAGES.MOVEMENT:
          if (!isCurrentPlayerOwner) {
            dispatch(deselectUnit());
          }
          break;
        case WAR_PHASE_STAGES.ABILITIES:
          // Выбор мог пережить переход MOVEMENT → ABILITIES (см. ревью
          // блокера "любой захваченный отряд может соблазнять") - захваченный
          // отряд чужой фракции не должен оставаться выбранным на этапе
          // способностей, доступном только его собственной фракции. Гоблины
          // добавлены сюда же по тому же принципу (add-goblins-faction) -
          // без этого выбранный гоблин при входе в ABILITIES сбрасывался бы
          // этой же проверкой, написанной изначально только под Суккубов
          if (!isCurrentPlayerOwner
            || (!isUnitFaction(selectedUnit, FACTIONS.SUCCUBES) && !isUnitFaction(selectedUnit, FACTIONS.GOBLINS))
          ) {
            dispatch(deselectUnit());
          }
          break;
        case WAR_PHASE_STAGES.SHOOTING:
          if (!isCurrentPlayerOwner
            || (selectedUnit.type !== UNIT_TYPES.D4.id && !checkIfUnitMeleeElf(selectedUnit))
          ) {
            dispatch(deselectUnit());
          }
          break;
        case WAR_PHASE_STAGES.MANEUVER:
          if (!isCurrentPlayerOwner || !selectedUnit.additionalSupply) {
            dispatch(deselectUnit());
          }
          break;
        default:
      }
    }

    if (selectedFarmHexKey && phase === PHASES.SUPPLY) {
      // Одновременная фаза, а раздавать клиенту уже не за кого (свою часть
      // фазы игрок закончил) — выбор фермы больше ни к чему
      if (isParallelSupplyPhase && !supplyPlayer) {
        dispatch(deselectFarm());
        return;
      }
      const remainingSupply = calcFarmRemainingSupply(selectedFarmHexKey, selectedFarmsForConversion, hexes, specialBuildings, players, units, citiesCache, supplyPlayer, pendingSupply, buildingsDamage);

      // Сбрасываем выбранную ферму только если у неё больше нет снабжения
      const shouldResetFarm = remainingSupply <= 0;

      if (shouldResetFarm) {
        dispatch(deselectFarm());
      }
    }
  }, [
    dispatch,
    selectedUnit,
    phase,
    warPhaseStage,
    prevWarPhaseStage,
    currentPlayer,
    supplyPlayer,
    isParallelSupplyPhase,
    buildingsDamage,
    citiesCache,
    hexes,
    pendingSupply,
    players,
    selectedFarmHexKey,
    specialBuildings,
    units,
    selectedFarmsForConversion,
  ]);

  return null;
};

export default EntitiesAutoSelector;
